import { useMemo, useState } from 'react';

import {
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  AppstoreOutlined,
  LayoutOutlined,
} from '@ant-design/icons';
import {
  Layout,
  Button,
  Modal,
  Spin,
  Image,
  Avatar,
  Dropdown,
  Skeleton,
  MenuProps,
  Menu,
  Switch,
} from 'antd';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { AstriHubLogo } from '@/components/general/AstriHubLogo';
import { ThemeButton } from '@/components/general/ThemeButton';
import { SidenavSlotContext } from '@/components/navigation/Sidenav/slot';
import { IS_DEVELOPMENT } from '@/config/environment';
import { ROUTES } from '@/config/routes';
import { MenuItem } from '@/entities/primitives';
import { useMyCompanies } from '@/features/login/api/getMyCompanies';
import { useUser } from '@/features/users/api/getUser';
import { useDisclosure } from '@/hooks/useDisclosure';
import { usePermissions } from '@/hooks/usePermissions';
import { useStorage } from '@/hooks/useStorage';
import { queryClient } from '@/lib/react-query';
import { useLayoutVersionStore } from '@/stores/useLayoutVersionStore';
import { useViewStore } from '@/stores/useViewStore';
import { logout } from '@/utils/logout';

import { ChangeCompanyDropdown } from './ChangeCompanyDropdown';
import { NewLayoutAnnouncement } from './NewLayoutAnnouncement';
import styles from './style.module.less';

const { Header } = Layout;

const companiesLink = ROUTES.getLink(`admin.company.list`);
const usersLink = ROUTES.getLink(`admin.users.list`);
const statusLink = ROUTES.getLink(`admin.status.root`);
const adminAILink = ROUTES.getLink(`admin.ai.dashboard`);
const workrLink = ROUTES.getLink(`admin.workr.dashboard`);
const irmLink = ROUTES.getLink(`irm`);
const astridLink = ROUTES.getLink(`irm.astrid`);
const webcastRoomsLink = ROUTES.getLink(`irm.videoconference.rooms`);
const astridConversationsLink = ROUTES.getLink(`irm.astrid.conversations`);
const servicesLink = ROUTES.getLink(`services`);
const automationLink = ROUTES.getLink(`automation`);
const agendaLink = ROUTES.getLink(`admin.agenda.list`);

const navItems: MenuItem[] = [
  {
    label: <Link to={companiesLink}>Empresas</Link>,
    key: companiesLink,
    permission: `companies`,
  },
  {
    label: <Link to={usersLink}>Usuários</Link>,
    key: usersLink,
    permission: `users`,
  },
  ...(IS_DEVELOPMENT
    ? [
        {
          label: <Link to={statusLink}>Status</Link>,
          key: statusLink,
          permission: `*` as const,
        },
      ]
    : []),
  {
    label: <Link to={agendaLink}>Agenda</Link>,
    key: agendaLink,
    permission: `*` as const,
  },
  {
    label: <Link to={adminAILink}>I.A</Link>,
    key: adminAILink,
    permission: `admin`,
  },
  {
    label: <Link to={workrLink}>Workr</Link>,
    key: workrLink,
    permission: `admin`,
  },
];

// The classic layout keeps the tabs of before: one per module, each on its own permission. The new
// layout moved them all into the side menu, so it is left with the dev-only Automação. The Webcast
// and Inteligência Artificial tabs go straight to their screens, which live under the IRM now.
const irmTab: MenuItem = {
  label: <Link to={irmLink}>IRM</Link>,
  key: irmLink,
  permission: `irm`,
};

const webcastTab: MenuItem = {
  label: <Link to={webcastRoomsLink}>Webcast</Link>,
  key: webcastRoomsLink,
  permission: `webcast`,
};

const aiTab: MenuItem = {
  label: <Link to={astridConversationsLink}>Inteligência Artificial</Link>,
  key: astridConversationsLink,
  permission: `ai`,
};

const servicesTab: MenuItem = {
  label: <Link to={servicesLink}>Demais Serviços</Link>,
  key: servicesLink,
  permission: `services` as const,
};

// Aba Automação desativada por ora. Para reativar, basta trocar para `true` (continua só em dev).
const SHOW_AUTOMATION_TAB = false;

const automationTabs: MenuItem[] = [
  ...(IS_DEVELOPMENT && SHOW_AUTOMATION_TAB
    ? [
        {
          label: <Link to={automationLink}>Automação</Link>,
          key: automationLink,
          permission: `automation` as const,
        },
      ]
    : []),
];

export const MasterLayout = ({ children }: React.PropsWithChildren<Record<string, unknown>>) => {
  const myCompaniesQuery = useMyCompanies({});
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, close, open } = useDisclosure();
  const { canAccess, startingPage } = usePermissions();
  const { currentView, idCompany } = useViewStore(
    (state) => ({
      idCompany: state.company,
      currentView: state.view,
    }),
    shallow,
  );

  const { parsedToken } = useStorage();
  const userQuery = useUser({ userId: parsedToken?.userId });

  const { version, announcementSeen, setNewLayout, dismissAnnouncement } = useLayoutVersionStore(
    (state) => ({
      version: state.version,
      announcementSeen: state.announcementSeen,
      setNewLayout: state.setNewLayout,
      dismissAnnouncement: state.dismissAnnouncement,
    }),
    shallow,
  );
  const isNewLayout = version === `new`;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // In the new layout the side menu docks in its own full-height column, next to the header, and
  // takes the logo with it (the header keeps the logo on the pages that have no side menu). The
  // classic layout offers no slot, so the side menu renders in place under a header that carries
  // the logo. Both share the same tree, so switching never remounts the page.
  const [sidenavElement, setSidenavElement] = useState<HTMLDivElement | null>(null);
  const [hasDockedSidenav, setHasDockedSidenav] = useState(false);
  const sidenavSlot = useMemo(
    () => (isNewLayout ? { element: sidenavElement, setDocked: setHasDockedSidenav } : undefined),
    [isNewLayout, sidenavElement],
  );

  const currentCompany = currentView
    ? myCompaniesQuery.data?.data.data?.find((item) => item.id === idCompany)
    : undefined;
  const isIrm = location.pathname.startsWith(irmLink);
  const isAstrid = location.pathname.startsWith(astridLink);
  // The layouts only differ where a side menu exists, so it is announced on the IRM screens.
  const showAnnouncement = !announcementSeen && currentView === `company` && isIrm && !isMobile;

  // Desktop: the module tabs follow their permissions, Demais Serviços and Automação are always
  // there. Mobile: every tab follows the profile permissions.
  const moduleTabs = [irmTab, webcastTab, aiTab];
  const companyDesktopTabs = isNewLayout
    ? automationTabs
    : [...moduleTabs.filter((item) => canAccess(item.permission)), servicesTab, ...automationTabs];
  const companyMobileTabs = isNewLayout
    ? automationTabs
    : [...moduleTabs, servicesTab, ...automationTabs];

  const mobileNavItems = (currentView === `company` ? companyMobileTabs : navItems)
    .filter((item) => canAccess(item.permission))
    .map((item) => ({
      key: item.key,
      label: item.label,
      onClick: () => navigate(item.key),
    }));

  const avatarMenu: MenuProps['items'] = [
    {
      label: `Configurações`,
      key: `1`,
      icon: <SettingOutlined />,
      onClick: () => navigate(ROUTES.getLink(`profile.home`)),
    },
    {
      label: (
        <span className={styles.layoutSwitch}>
          Novo layout
          <Switch size="small" checked={isNewLayout} tabIndex={-1} />
        </span>
      ),
      key: `layout`,
      icon: <LayoutOutlined />,
      // The switch only shows the state; the item click is what toggles it.
      onClick: () => setNewLayout(!isNewLayout),
    },
    { label: `Sair`, key: `2`, icon: <LogoutOutlined />, danger: true, onClick: open },
  ];

  return (
    <Layout hasSider={isNewLayout} className="layout">
      {isNewLayout && <div ref={setSidenavElement} className={styles.sidenavSlot} />}

      <Layout>
        <Header
          className={classNames(styles.header, {
            [styles.headerBordered]: isNewLayout && isIrm,
            [styles.headerBorderedClassic]: !isNewLayout && isAstrid,
          })}
        >
          <div className="logo-prisma">
            {!hasDockedSidenav && (
              <>
                <AstriHubLogo className="desktop-logo" to={startingPage ?? `/`} />
                <AstriHubLogo variant="icon" className="mobile-logo" to={startingPage ?? `/`} />
              </>
            )}

            <div className={styles.header__left}>
              {/* desktop */}
              <div className={styles.header__desktop_nav}>
                {currentView === `company` ? (
                  companyDesktopTabs.length > 0 && (
                    <Menu
                      style={{ border: 'none' }}
                      mode="horizontal"
                      items={companyDesktopTabs}
                      className={styles.header__menu}
                      selectedKeys={[location.pathname]}
                    />
                  )
                ) : (
                  <Menu
                    mode="horizontal"
                    items={navItems.filter((item) => canAccess(item.permission))}
                    selectedKeys={[location.pathname]}
                    className={styles.header__menu}
                  />
                )}
              </div>

              {/* mobile */}
              <div className={styles.header__mobile_nav}>
                {mobileNavItems.length > 0 && (
                  <Dropdown
                    menu={{ items: mobileNavItems, selectedKeys: [location.pathname] }}
                    trigger={['click']}
                  >
                    <Button type="text" className="btn-mobile-swap" icon={<AppstoreOutlined />} />
                  </Dropdown>
                )}
              </div>
            </div>
          </div>

          <div className="others-itens-menu">
            <div className="theme-button-ml">
              <ThemeButton />
            </div>
            <div className={styles.header__logoClient}>
              {myCompaniesQuery.isLoading ? (
                <Spin size="default" />
              ) : (
                // logo empresa cliente
                <div className={styles.header__logo}>
                  <ChangeCompanyDropdown>
                    <Image
                      preview={false}
                      src={currentCompany?.more.logoURL}
                      className="logo-client"
                      alt={`Logo da empresa ${currentCompany?.name}`}
                      fallback="data:image/png;base64,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"
                    />
                  </ChangeCompanyDropdown>
                </div>
              )}
            </div>

            <div className={styles.header__right}>
              <Dropdown placement="bottomRight" menu={{ items: avatarMenu }}>
                <div className={styles[`user-menu`]}>
                  <Avatar
                    className="logo-user"
                    icon={<UserOutlined />}
                    src={userQuery.data?.data.data.imageUrl}
                  />
                  {userQuery.isLoading ? (
                    <Skeleton.Node style={{ width: 60, height: 20 }} active>
                      <div />
                    </Skeleton.Node>
                  ) : (
                    <span className="name-profile-header">{userQuery.data?.data.data.name}</span>
                  )}
                </div>
              </Dropdown>

              <Modal
                open={isOpen}
                onCancel={close}
                title=" Deseja realmente sair da sua conta?"
                footer={[
                  <Button key="cancel" onClick={close}>
                    Cancelar
                  </Button>,
                  <Button
                    key="ok"
                    type="primary"
                    onClick={() => {
                      close();
                      logout();
                      queryClient.setQueryData([`my-companies`], { data: [] });
                      navigate(`/login`);
                    }}
                  >
                    Sair da conta
                  </Button>,
                ]}
              >
                <p>
                  Ao sair da sua conta você será deslogado do sistema e precisará entrar novamente
                  com suas credenciais.
                </p>
              </Modal>
            </div>
          </div>
        </Header>

        <Layout>
          <SidenavSlotContext.Provider value={sidenavSlot}>{children}</SidenavSlotContext.Provider>
        </Layout>

        <NewLayoutAnnouncement
          open={showAnnouncement}
          onEnable={() => setNewLayout(true)}
          onDismiss={dismissAnnouncement}
        />
      </Layout>
    </Layout>
  );
};
